import { type Raster, RGBA_CHANNELS } from './raster';

/**
 * 마플 미리보기 샘플러(쌍선형). (sx, sy) 는 원본 픽셀 인덱스 좌표이고, 정수면 그 픽셀을 그대로 읽는다.
 * - 네 이웃 (x0, y0)·(x0+1, y0)·(x0, y0+1)·(x0+1, y0+1) 에 쌍선형 가중치를 쓴다. 이미지 밖 이웃은 마지막 열·행으로 자른다.
 * - 알파는 일반 쌍선형으로 섞는다.
 * - 완전 투명(α = 0) 이웃의 색은 나머지 이웃 색의 단순 평균으로 채운다. 그다음 색을 premultiplied 가 아닌 직선 쌍선형으로 섞는다.
 * 결과는 target[offset..offset+3] 에 8비트(반올림, .5 는 짝수)로 쓴다. target 은 호출자가 새로 만든 출력 버퍼다.
 * 픽셀마다 불리므로 배열을 만들지 않는다. 더하는 순서는 왼쪽 위·오른쪽 위·왼쪽 아래·오른쪽 아래로 고정한다.
 */
export function sampleMarppleBilinear(
  source: Raster,
  sx: number,
  sy: number,
  target: Uint8ClampedArray,
  offset: number,
): void {
  const { width, height, data } = source;
  const x0 = Math.floor(sx);
  const y0 = Math.floor(sy);
  const fx = sx - x0;
  const fy = sy - y0;
  const x1 = Math.min(x0 + 1, width - 1);
  const y1 = Math.min(y0 + 1, height - 1);
  const topLeft = (y0 * width + x0) * RGBA_CHANNELS;
  const topRight = (y0 * width + x1) * RGBA_CHANNELS;
  const bottomLeft = (y1 * width + x0) * RGBA_CHANNELS;
  const bottomRight = (y1 * width + x1) * RGBA_CHANNELS;
  const wTopLeft = (1 - fx) * (1 - fy);
  const wTopRight = fx * (1 - fy);
  const wBottomLeft = (1 - fx) * fy;
  const wBottomRight = fx * fy;
  const aTopLeft = data[topLeft + 3] ?? 0;
  const aTopRight = data[topRight + 3] ?? 0;
  const aBottomLeft = data[bottomLeft + 3] ?? 0;
  const aBottomRight = data[bottomRight + 3] ?? 0;
  target[offset + 3] =
    wTopLeft * aTopLeft +
    wTopRight * aTopRight +
    wBottomLeft * aBottomLeft +
    wBottomRight * aBottomRight;
  const liveCount =
    (aTopLeft > 0 ? 1 : 0) +
    (aTopRight > 0 ? 1 : 0) +
    (aBottomLeft > 0 ? 1 : 0) +
    (aBottomRight > 0 ? 1 : 0);
  for (let channel = 0; channel < 3; channel += 1) {
    if (liveCount === 0) {
      target[offset + channel] = 0;
      continue;
    }
    const cTopLeft = data[topLeft + channel] ?? 0;
    const cTopRight = data[topRight + channel] ?? 0;
    const cBottomLeft = data[bottomLeft + channel] ?? 0;
    const cBottomRight = data[bottomRight + channel] ?? 0;
    if (liveCount === 4) {
      // 투명 이웃이 없으면 채울 색이 없어 일반 쌍선형과 같다. 불투명 디자인 대부분이 이 경로다.
      target[offset + channel] =
        wTopLeft * cTopLeft +
        wTopRight * cTopRight +
        wBottomLeft * cBottomLeft +
        wBottomRight * cBottomRight;
      continue;
    }
    const fill =
      ((aTopLeft > 0 ? cTopLeft : 0) +
        (aTopRight > 0 ? cTopRight : 0) +
        (aBottomLeft > 0 ? cBottomLeft : 0) +
        (aBottomRight > 0 ? cBottomRight : 0)) /
      liveCount;
    target[offset + channel] =
      wTopLeft * (aTopLeft > 0 ? cTopLeft : fill) +
      wTopRight * (aTopRight > 0 ? cTopRight : fill) +
      wBottomLeft * (aBottomLeft > 0 ? cBottomLeft : fill) +
      wBottomRight * (aBottomRight > 0 ? cBottomRight : fill);
  }
}
